import { Delete } from '@mui/icons-material';
import { Breadcrumbs, IconButton, Link, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, Tabs, TextField, Tooltip } from "@mui/material";
import { useAtom } from 'jotai';
import { ChangeEvent, FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { modalDeleteIgnoredDivergence, permissionsAtom } from '../../atoms/atom';
import AppBar from "../../components/app-bar/AppBar";
import DeleteIgnoredDivergenceModal from "../../components/comparation/DeleteIgnoredDivergenceModal";
import { ComparationService } from "../../services/ComparationService";
import { ComparationAudit } from "../../types/ComparationAudit";
import { getDivergenceFieldLabel, IgnoredDivergence } from "../../types/IgnoredDivergence";
import { useUtils } from "../../utils/utilFunctions";
import { getLoading } from '../execution-events/ExecutionEvents';
import './ComparationAudits.scss';

type AuditColumn = {
	label: string;
	key: keyof ComparationAudit;
};

const columns: AuditColumn[] = [
	{ label: 'Data e hora', key: 'createdAt' },
	{ label: 'Usuário', key: 'accountName' },
	{ label: 'Funcionário', key: 'employeeName' },
	{ label: 'CPF', key: 'cpf' },
	{ label: 'Empresa', key: 'companyName' },
	{ label: 'Ação', key: 'action' },
	{ label: 'Campo', key: 'field' },
	{ label: 'Valor anterior', key: 'previousValue' },
	{ label: 'Novo valor', key: 'newValue' }
];
const actionLabels: { [key: string]: string } = {
	DISMISSAL: 'Desligamento',
	ABSENCE: 'Afastamento',
	ABSENCE_RETURN: 'Retorno de afastamento',
	POSITION_CHANGE: 'Alteração de cargo',
	POSITION_CREATION: 'Criação de cargo',
	TRANSFER: 'Transferência',
	UPDATE: 'Atualização cadastral'
};
const pageSizes = [25, 50, 100];
const sggEmployeeUrl = 'https://app.sgg.net.br/index.php?func=funcionario&origem=pesquisa_visual&id_funcionario=';

const ComparationAudits: FC = () => {
	const { id } = useParams<{ id: string }>();
	const [audits, setAudits] = useState<ComparationAudit[]>([]);
	const [total, setTotal] = useState(0);
	const [search, setSearch] = useState('');
	const [debouncedSearch, setDebouncedSearch] = useState('');
	const [sort, setSort] = useState<keyof ComparationAudit>('createdAt');
	const [direction, setDirection] = useState<'asc' | 'desc'>('desc');
	const [page, setPage] = useState(0);
	const [size, setSize] = useState(pageSizes[0]);
	const [loading, setLoading] = useState(true);
	const [tab, setTab] = useState('UPDATES');
	const [ignoredDivergences, setIgnoredDivergences] = useState<IgnoredDivergence[]>([]);
	const [selectedIgnored, setSelectedIgnored] = useState<IgnoredDivergence>();
	const [loadingIgnored, setLoadingIgnored] = useState(true);
	const [permissions] = useAtom(permissionsAtom);
	const [, setOpenDeleteIgnored] = useAtom(modalDeleteIgnoredDivergence);
	const comparationService = new ComparationService();
	const { verifyPermission, formatIsoDate, formatCbo } = useUtils();
	const navigate = useNavigate();

	const back = () => {
		navigate(-1);
	}

	const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
	}

	const handleSort = (key: keyof ComparationAudit) => {
		setDirection(sort === key && direction === 'desc' ? 'asc' : 'desc');
		setSort(key);
		setPage(0);
	}

	const handleChangeSize = (event: ChangeEvent<HTMLInputElement>) => {
		setSize(Number(event.target.value));
		setPage(0);
	}

	const employeeLink = (audit: ComparationAudit) => {
		if (!audit.employeeId) {
			return audit.employeeName;
		}
		return (
			<Tooltip title="Ver funcionário no SGG">
				<Link
					href={`${sggEmployeeUrl}${audit.employeeId}`}
					target="_blank"
					rel="noopener noreferrer"
					underline="hover"
					color="primary"
				>
					{audit.employeeName}
				</Link>
			</Tooltip>
		);
	};

	const formatIgnoredValue = (field: string, value: string | undefined) => field === 'cbo'
		? formatCbo(value || '-')
		: formatIsoDate(value || '-');

	const handleDelete = (ignored: IgnoredDivergence) => {
		setSelectedIgnored(ignored);
		setOpenDeleteIgnored(true);
	}

	const loadIgnoredDivergences = async () => {
		if (!id) {
			return;
		}
		setLoadingIgnored(true);
		const response = await comparationService.getIgnoredDivergences(Number(id));
		setIgnoredDivergences(response && response.data ? response.data : []);
		setLoadingIgnored(false);
	};

	const loadAudits = async () => {
		if (!id) {
			return;
		}
		setLoading(true);
		const response = await comparationService.getAudits({
			executionId: Number(id),
			search: debouncedSearch,
			sort: sort,
			direction: direction,
			page: page,
			size: size
		});
		setAudits(response && response.data ? response.data.content : []);
		setTotal(response && response.data ? response.data.total : 0);
		setLoading(false);
	};

	useEffect(() => {
		const timeout = setTimeout(() => {
			setDebouncedSearch(search);
			setPage(0);
		}, 500);
		return () => clearTimeout(timeout);
	}, [search]);

	useEffect(() => {
		loadAudits();
	}, [id, debouncedSearch, sort, direction, page, size]);

	useEffect(() => {
		loadIgnoredDivergences();
	}, [id]);

	useEffect(() => {
		if (!!permissions.length) {
			verifyPermission(permissions, 'MANAGE_COMPARATIONS');
		}
	}, []);

	return(
		<div className="comparation-audits">
			<AppBar
				modulo="Comparações"
			/>
			<div className="component">
				<div className="top">
					<div className="etapas">
						<Breadcrumbs aria-label="breadcrumb" sx={{display: "flex", alignItems: "flex-end"}}>
							<Link variant="h6" sx={{cursor: "pointer"}} underline="hover" color="inherit" onClick={() => back()}>
								Comparações
							</Link>
							<Link
								variant="subtitle1"
								underline="hover"
								color="text.primary"
								aria-current="page"
								sx={{display: "flex", alignItems: "flex-end !important"}}
							>
								Auditoria
							</Link>
						</Breadcrumbs>
					</div>
					<div className="search">
						{tab === 'UPDATES' && <TextField
							sx={{width: { xs: '100%', md: '305px' }}}
							label="Buscar funcionário pelo nome ou CPF"
							id="audit-search"
							size="small"
							value={search}
							onChange={handleChangeSearch}
						/>}
					</div>
				</div>
				<Tabs
					value={tab}
					onChange={(_, newTab) => setTab(newTab)}
					textColor="secondary"
				>
					<Tab label="Atualizações realizadas" value="UPDATES"/>
					<Tab label="Divergências ignoradas" value="IGNORED"/>
				</Tabs>
				{tab === 'IGNORED'
					? <div className="listagem">
						{loadingIgnored
							? getLoading()
							: !ignoredDivergences.length
								? <div className="sem-registros">Nenhuma divergência ignorada para esta execução...</div>
								: <TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
									<Table stickyHeader size="small" aria-label="divergências ignoradas">
										<TableHead>
											<TableRow>
												<TableCell className="th-styled">Data e hora</TableCell>
												<TableCell className="th-styled">Usuário</TableCell>
												<TableCell className="th-styled">CNPJ</TableCell>
												<TableCell className="th-styled">Campo</TableCell>
												<TableCell className="th-styled">Valor no SGG</TableCell>
												<TableCell className="th-styled">Valor no eSocial</TableCell>
												<TableCell className="th-styled" align="center">Remover</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{ignoredDivergences.map(ignored => (
												<TableRow key={ignored.id} className="row-line">
													<TableCell className="td-styled">{ignored.createdAt}</TableCell>
													<TableCell className="td-styled">{ignored.accountName || ignored.username || '-'}</TableCell>
													<TableCell className="td-styled">{ignored.cnpj}</TableCell>
													<TableCell className="td-styled">{getDivergenceFieldLabel(ignored.field)}</TableCell>
													<TableCell className="td-styled">{formatIgnoredValue(ignored.field, ignored.sggValue)}</TableCell>
													<TableCell className="td-styled">{formatIgnoredValue(ignored.field, ignored.esocialValue)}</TableCell>
													<TableCell className="td-styled" align="center">
														<Tooltip title="Remover regra">
															<IconButton color="inherit" onClick={() => handleDelete(ignored)}>
																<Delete sx={{fontSize: '2.5vh'}} color="error"/>
															</IconButton>
														</Tooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
						}
					</div>
					: <div className="listagem">
						{loading
							? getLoading()
							: !audits.length
								? <div className="sem-registros">Nenhuma atualização auditada para esta execução...</div>
								: <TableContainer sx={{ maxHeight: 700, boxShadow: "0px 0px 1px 1px #b6b6b6" }} component={Paper}>
									<Table stickyHeader size="small" aria-label="auditoria das atualizações">
										<TableHead>
											<TableRow>
												{columns.map(column => (
													<TableCell key={column.key} className="th-styled">
														<TableSortLabel
															active={sort === column.key}
															direction={sort === column.key ? direction : 'desc'}
															onClick={() => handleSort(column.key)}
														>
															{column.label}
														</TableSortLabel>
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{audits.map(audit => (
												<TableRow key={audit.id} className="row-line">
													<TableCell className="td-styled">{audit.createdAt}</TableCell>
													<TableCell className="td-styled">{audit.accountName || audit.username || '-'}</TableCell>
													<TableCell className="td-styled">{employeeLink(audit)}</TableCell>
													<TableCell className="td-styled">{audit.cpf}</TableCell>
													<TableCell className="td-styled">{audit.companyName || '-'}</TableCell>
													<TableCell className="td-styled">{actionLabels[audit.action] || audit.action}</TableCell>
													<TableCell className="td-styled">{audit.field || '-'}</TableCell>
													<TableCell className="td-styled">{formatIsoDate(audit.previousValue || '-')}</TableCell>
													<TableCell className="td-styled">{formatIsoDate(audit.newValue || '-')}</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
						}
						<TablePagination
							component="div"
							rowsPerPageOptions={pageSizes}
							count={total}
							rowsPerPage={size}
							page={page}
							onPageChange={(_, newPage) => setPage(newPage)}
							onRowsPerPageChange={handleChangeSize}
							labelRowsPerPage="Registros por página"
							labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
						/>
					</div>
				}
			</div>
			<DeleteIgnoredDivergenceModal ignored={selectedIgnored} onDeleted={loadIgnoredDivergences}/>
		</div>
	);
}

export default ComparationAudits;